/**
 * Addressbook Section Component
 * Smart component - wires up Redux actions to dumb components
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from 'react';
import { useSelector, useDispatch } from '@ufc/core';
import { selectIsAdmin } from '@ufc/domains/admin-context';
import { selectAllowedRegionIds } from '@ufc/domains/portal-init';
import { useTranslation, useIsRTL } from '@ufc/core';
import { exportToXlsx, type ExportColumn } from '@ufc/core';
import { toast, PaginationControls } from '@ufc/uikit';

import {
  selectFilteredAddresses,
  selectAddressStats,
  selectAddressbookLoading,
  selectAddressbookError,
  selectAddressesFilter,
  selectAddressesPagination,
  selectSelectedAddress,
  selectCities,
  selectLocationTypes,
} from '../../../store/addressbook.selectors';
import { addressbookActions } from '../../../store/addressbook.slice';
import { addressbookApi } from '../../../infrastructure/api/addressbook-api';
import { useAddressbookReadable } from '../../../copilot/addressbook.readable';
import { useAddressbookActions } from '../../../copilot/addressbook.actions';
import { useAddressbookSuggestions } from '../../../copilot/addressbook.suggestions';
const AddressDetailDrawer = lazy(() =>
  import('../../drawers/address-detail-drawer').then((m) => ({
    default: m.AddressDetailDrawer,
  })),
);
const AddressFormDrawer = lazy(() =>
  import('../../drawers/address-form-drawer').then((m) => ({
    default: m.AddressFormDrawer,
  })),
);
import { DeleteAddressDialog } from '../delete-address-dialog';
import type {
  Address,
  AddressesFilter,
  LocationType,
  LocationStatus,
  CreateAddressRequest,
} from '../../../domain/types';

import { buildStatusFilterOptions } from './utils';
import {
  buildTypeFilterOptions,
  buildCityFilterOptions,
  getActiveAddressFilterCount,
} from './utils';
import {
  AddressbookHeader,
  AddressbookStats,
  AddressbookToolbar,
  AddressbookFilterDrawer,
  AddressbookTable,
  AddressbookEmptyState,
} from './components';

// ============================================================================
// Helpers
// ============================================================================

function filterAddressesByAllowedRegions(
  addresses: Address[],
  allowedRegionIds: string[],
  isAdmin: boolean,
): Address[] {
  if (isAdmin) return addresses;
  if (allowedRegionIds.length === 0) return addresses;
  return addresses.filter(
    (address) =>
      address.regionId && allowedRegionIds.includes(address.regionId),
  );
}

function resolveLocationTypeCode(
  selectedType: string,
  locationTypes: LocationType[],
): string | undefined {
  if (!selectedType || selectedType === 'all') {
    return undefined;
  }

  const selected = locationTypes.find(
    (locationType) =>
      locationType.code.toLowerCase() === selectedType.toLowerCase(),
  );
  if (selected) {
    return selected.code;
  }

  const fallbackMap: Record<string, string> = {
    plant: 'factory_location',
    customer: 'CM',
  };
  return fallbackMap[selectedType];
}

async function handleAddressExport(filter: AddressesFilter, isAdmin: boolean) {
  try {
    toast({ title: 'Exporting...', description: 'Fetching all records' });
    const data = await addressbookApi.exportAll(filter);
    const columns: ExportColumn<Address>[] = [
      { header: 'Code', key: 'code', width: 12 },
      { header: 'Name', key: 'name', width: 24 },
      { header: 'Address', key: 'address', width: 32 },
      { header: 'City', key: 'city', width: 18 },
      { header: 'Postal Code', key: 'postalCode', width: 12 },
      { header: 'Contact Name', key: 'contactName', width: 20 },
      { header: 'Contact Phone', key: 'contactPhone', width: 16 },
      { header: 'Contact Email', key: 'contactEmail', width: 24 },
      { header: 'Status', key: 'status', width: 10 },
    ];
    if (isAdmin) {
      columns.unshift({
        header: 'Customer',
        accessor: (a) => a.tenant?.name ?? '',
        width: 20,
      });
    }
    exportToXlsx({
      fileName: 'addressbook',
      sheetName: 'Addresses',
      columns,
      rows: data,
    });
  } catch (error) {
    console.error('Export failed:', error);
    toast({
      title: 'Export failed',
      description: 'Could not export addresses',
      variant: 'destructive',
    });
  }
}

// ============================================================================
// Component
// ============================================================================

export function AddressbookSection() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = useIsRTL();
  const isArabic = i18n.language === 'ar';

  // Redux state
  const addressesFromStore = useSelector(selectFilteredAddresses);
  const stats = useSelector(selectAddressStats);
  const loading = useSelector(selectAddressbookLoading);
  const error = useSelector(selectAddressbookError);
  const filter = useSelector(selectAddressesFilter);
  const pagination = useSelector(selectAddressesPagination);
  const selectedAddressFromStore = useSelector(selectSelectedAddress);
  const cities = useSelector(selectCities);
  const locationTypes = useSelector(selectLocationTypes);
  const isAdmin = useSelector(selectIsAdmin);
  const allowedRegionIds = useSelector(selectAllowedRegionIds);

  // Local state
  const [searchValue, setSearchValue] = useState(filter.search || '');
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Drawer states
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const drawerOpeningRef = useRef(false);
  const formDrawerOpeningRef = useRef(false);
  const isEditingRef = useRef(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);

  // Fetch reference data on mount
  useEffect(() => {
    dispatch(addressbookActions.fetchLocationTypes());
    dispatch(addressbookActions.fetchCountries());
    dispatch(addressbookActions.fetchCities());
  }, [dispatch]);

  const effectiveAllowedRegionIds = useMemo(
    () => (isAdmin ? [] : allowedRegionIds),
    [isAdmin, allowedRegionIds],
  );

  const addresses = useMemo(
    () =>
      filterAddressesByAllowedRegions(
        addressesFromStore,
        effectiveAllowedRegionIds,
        isAdmin,
      ),
    [addressesFromStore, effectiveAllowedRegionIds, isAdmin],
  );

  // Fetch addresses when filter/pagination changes
  useEffect(() => {
    dispatch(addressbookActions.fetchAddresses(filter));
  }, [
    dispatch,
    filter.page,
    filter.pageSize,
    filter.type,
    filter.locationTypeCode,
    filter.status,
    filter.search,
    filter.cityId,
  ]);

  useEffect(() => {
    setSearchValue(filter.search || '');
  }, [filter.search]);

  // Fetch stats separately (not affected by pagination)
  useEffect(() => {
    dispatch(addressbookActions.fetchStats());
  }, [dispatch, filter.type, filter.status]);

  // Copilot integrations
  useAddressbookReadable();
  useAddressbookActions();
  useAddressbookSuggestions();

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      dispatch(addressbookActions.searchAddresses(value));
    },
    [dispatch],
  );

  const handleStatusFilter = useCallback(
    (status: string) => {
      dispatch(addressbookActions.setPage(1)); // Reset to first page on filter change
      dispatch(
        addressbookActions.filterByStatus(status as LocationStatus | 'all'),
      );
    },
    [dispatch],
  );

  const handleTypeFilterChange = useCallback(
    (type: string) => {
      const locationTypeCode = resolveLocationTypeCode(type, locationTypes);
      dispatch(addressbookActions.setPage(1));
      dispatch(
        addressbookActions.fetchAddresses({
          type: type as AddressesFilter['type'],
          locationTypeCode,
        }),
      );
    },
    [dispatch, locationTypes],
  );

  const handleCityFilterChange = useCallback(
    (cityId: string) => {
      const nextValue = cityId === 'all' ? undefined : cityId;
      dispatch(addressbookActions.filterByCityId(nextValue));
    },
    [dispatch],
  );

  const handleClearFilters = useCallback(() => {
    dispatch(
      addressbookActions.setFilter({
        ...filter,
        type: 'all',
        locationTypeCode: undefined,
        status: 'all',
        cityId: undefined,
        page: 1,
      }),
    );
  }, [dispatch, filter]);

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(addressbookActions.setPage(page));
    },
    [dispatch],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      dispatch(addressbookActions.setPageSize(pageSize));
    },
    [dispatch],
  );

  const handleRowClick = useCallback(
    (address: Address) => {
      // Don't open detail drawer if we're in the process of opening edit form
      if (isEditingRef.current) {
        return;
      }
      setSelectedRowId(address.id);
      setSelectedAddress(address);
      dispatch(addressbookActions.setSelectedAddress(address));
      drawerOpeningRef.current = true;
      setDetailDrawerOpen(true);
      // Reset the flag after a short delay to allow drawer to open
      setTimeout(() => {
        drawerOpeningRef.current = false;
      }, 300);
    },
    [dispatch],
  );

  const handleRowDoubleClick = useCallback(
    (address: Address) => {
      if (selectedRowId === address.id) {
        setSelectedRowId(null);
        setSelectedAddress(null);
        setDetailDrawerOpen(false);
      }
    },
    [selectedRowId],
  );

  // Add new address
  const handleAddClick = useCallback(() => {
    setEditingAddress(null);
    setFormDrawerOpen(true);
  }, []);

  // Edit address
  const handleEditClick = useCallback(
    (address: Address) => {
      // Set flag to prevent row click from opening detail drawer
      isEditingRef.current = true;

      // Explicitly close detail drawer first to prevent it from opening
      setDetailDrawerOpen(false);
      setSelectedRowId(null);

      // Always fetch fresh data so Arabic localized fields (nameI18n.ar, etc.) are populated
      dispatch(addressbookActions.fetchAddress(address.documentId));
      // Open form drawer immediately with current address data (Arabic fields will
      // be filled in once fetchAddress completes and the effect below runs)
      setEditingAddress(address);
      formDrawerOpeningRef.current = true;
      setFormDrawerOpen(true);
      // Reset the flags after a short delay to allow drawer to open
      setTimeout(() => {
        formDrawerOpeningRef.current = false;
        isEditingRef.current = false;
      }, 300);
    },
    [dispatch],
  );

  // Delete address
  const handleDeleteClick = useCallback((address: Address) => {
    setAddressToDelete(address);
    setDetailDrawerOpen(false);
    setDeleteDialogOpen(true);
  }, []);

  // Form submit (create/update)
  const handleFormSubmit = useCallback(
    (data: CreateAddressRequest) => {
      if (editingAddress) {
        // Update existing
        dispatch(
          addressbookActions.updateAddress({
            id: editingAddress.id,
            documentId: editingAddress.documentId,
            data: {
              name: data.name,
              locationTypeId: data.locationTypeId,
              addressLine1: data.addressLine1,
              addressLine2: data.addressLine2,
              cityId: data.cityId,
              countryId: data.countryId,
              postalCode: data.postalCode,
              timezone: data.timezone,
              coordinates: data.coordinates,
              contactName: data.contactName,
              contactPhone: data.contactPhone,
              isDefault: data.isDefault,
            },
          }),
        );
      } else {
        // Create new
        dispatch(addressbookActions.createAddress(data));
      }
      setFormDrawerOpen(false);
      setEditingAddress(null);
    },
    [dispatch, editingAddress],
  );

  // Update editing address with full data (including Arabic fields) when fetchAddress completes
  useEffect(() => {
    if (!formDrawerOpen) return;
    if (!selectedAddressFromStore) return;
    if (editingAddress?.documentId !== selectedAddressFromStore.documentId)
      return;
    setEditingAddress(selectedAddressFromStore);
  }, [formDrawerOpen, editingAddress?.documentId, selectedAddressFromStore]);

  // Confirm delete
  const handleDeleteConfirm = useCallback(
    (address: Address) => {
      dispatch(
        addressbookActions.deleteAddress({
          id: address.id,
          documentId: address.documentId,
        }),
      );
      setDeleteDialogOpen(false);
      setAddressToDelete(null);
    },
    [dispatch],
  );

  // Export
  const handleExport = useCallback(() => {
    handleAddressExport(filter, isAdmin);
  }, [filter, isAdmin]);

  // Retry on error
  const handleRetry = useCallback(() => {
    dispatch(addressbookActions.fetchAddresses(undefined));
  }, [dispatch]);

  // ============================================================================
  // Render
  // ============================================================================

  const statusFilterOptions = buildStatusFilterOptions(t);
  const typeFilterOptions = buildTypeFilterOptions(t);
  const cityFilterOptions = buildCityFilterOptions(cities, t);
  const activeFilterCount = getActiveAddressFilterCount(filter);

  // Loading state
  if (loading && addresses.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Error state
  if (error && addresses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="text-destructive mb-4">
          <svg
            className="w-16 h-16 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('common.error')}
        </h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 safe-top safe-left safe-right">
      {/* Header */}
      <AddressbookHeader
        title={t('addressbook.title')}
        subtitle={t('addressbook.subtitle')}
        isRTL={isRTL}
        onExport={handleExport}
        onAdd={handleAddClick}
        exportLabel={t('common.export')}
        addLabel={t('addressbook.addAddress')}
      />

      {/* Stats */}
      <AddressbookStats stats={stats} />

      {/* Toolbar */}
      <AddressbookToolbar
        searchPlaceholder={t('addressbook.searchPlaceholder')}
        searchValue={searchValue}
        onSearchChange={handleSearch}
        activeFilterCount={activeFilterCount}
        filterButtonLabel={t('common.filter')}
        onOpenFilterDrawer={() => setFilterDrawerOpen(true)}
        statusFilterOptions={statusFilterOptions}
        statusFilterValue={filter.status || 'all'}
        onStatusFilterChange={handleStatusFilter}
        isRTL={isRTL}
      />

      <AddressbookFilterDrawer
        open={filterDrawerOpen}
        onOpenChange={setFilterDrawerOpen}
        title={t('common.filter')}
        subtitle={t('addressbook.subtitle')}
        statusLabel={t('common.status')}
        typeLabel={t('addressbook.locationType')}
        cityLabel={t('addressbook.city')}
        statusOptions={statusFilterOptions}
        typeOptions={typeFilterOptions}
        cityOptions={cityFilterOptions}
        selectedStatus={filter.status || 'all'}
        selectedType={filter.type || 'all'}
        selectedCityId={filter.cityId}
        activeFilterCount={activeFilterCount}
        clearLabel={t('common.clear')}
        isRTL={isRTL}
        onStatusChange={handleStatusFilter}
        onTypeChange={handleTypeFilterChange}
        onCityChange={handleCityFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Table or Empty State */}
      {addresses.length === 0 ? (
        <AddressbookEmptyState
          title={t('addressbook.noAddresses')}
          description={t('addressbook.noAddressesDescription')}
          actionLabel={t('addressbook.addFirst')}
          onAction={handleAddClick}
        />
      ) : (
        <AddressbookTable
          addresses={addresses}
          selectedRowId={selectedRowId}
          showTenantColumn={isAdmin}
          onRowClick={handleRowClick}
          onRowDoubleClick={handleRowDoubleClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          isArabic={isArabic}
        />
      )}

      {/* Pagination */}
      {addresses.length > 0 && (
        <PaginationControls
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.total}
          totalPages={pagination.pageCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizes={[10, 20, 50, 100]}
          isRTL={isRTL}
        />
      )}

      {/* Detail Drawer */}
      <Suspense fallback={null}>
        <AddressDetailDrawer
          address={selectedAddress}
          open={detailDrawerOpen}
          onOpenChange={(open) => {
            // Prevent closing if we're in the process of opening
            if (!open && drawerOpeningRef.current) {
              return;
            }
            setDetailDrawerOpen(open);
            if (!open) setSelectedRowId(null);
          }}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
        />
      </Suspense>

      {/* Form Drawer (Add/Edit) */}
      <Suspense fallback={null}>
        <AddressFormDrawer
          open={formDrawerOpen}
          onOpenChange={(open) => {
            // Prevent closing if we're in the process of opening
            if (!open && formDrawerOpeningRef.current) {
              return;
            }
            setFormDrawerOpen(open);
            if (!open) {
              setEditingAddress(null);
            }
          }}
          address={editingAddress}
          cities={cities}
          locationTypes={locationTypes}
          allowedRegionIds={effectiveAllowedRegionIds}
          onSubmit={handleFormSubmit}
          isLoading={loading}
          showLocationType={isAdmin}
          isRTL={isRTL}
        />
      </Suspense>

      {/* Delete Confirmation Dialog */}
      <DeleteAddressDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        address={addressToDelete}
        onConfirm={handleDeleteConfirm}
        isLoading={loading}
        isRTL={isRTL}
      />
    </div>
  );
}
